// src/components/CombinationDetails.jsx

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import KursplanPage from './Kursplan';
import "../styles/ClassDetail.css"; // Используем те же стили, что и ClassDetail / Verwendung der gleichen Stile wie in ClassDetail
import Loader from '../components/Loader';

const CombinationDetails = () => {
    const { id } = useParams(); // Получаем id комбинации из URL / Abrufen der Kombinations-ID aus der URL
    const [ausbildungen, setAusbildungen] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [fragen, setFragen] = useState([]);
    const [combinationData, setCombinationData] = useState(null); // Аналогично тому, как было klasseData / Ähnlich wie bei klasseData
    const [selectedAusbildungId, setSelectedAusbildungId] = useState(null);
    const [selectedAusbildungGW, setSelectedAusbildungGW] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const navigate = useNavigate(); // useNavigate

    // Состояния для вопросов / Zustände für Fragen
    const [openQuestions, setOpenQuestions] = useState(new Set());
    const [showAllQuestions, setShowAllQuestions] = useState(false);

    // URL для получения Ausbildungen, связанных с комбинацией id / URL zum Abrufen der Ausbildungen, die mit der Kombinations-ID verknüpft sind
    // Примерно как сделано в ClassDetail, но меняем на Kombi / Ähnlich wie in ClassDetail, aber mit Änderungen für Kombi
    const KOMBI_AUSBILDUNGEN_URL = `https://admin.thirsty-shtern.91-151-16-181.plesk.page/items/Ausbildungen?filter[Kombi][_eq]=${id}&fields=*,Blocks.*,Blocks.item.*,Blocks.item.*.*,Blocks.item.*.*.*,Fragen.*,Fragen.Fragen_id.*&sort=sort`;

    // URL для получения самой комбинации / URL zum Abrufen der Kombination selbst
    const KOMBI_DETAIL_URL = `https://admin.thirsty-shtern.91-151-16-181.plesk.page/items/Kombinationen/${id}?fields=*,Klassen.*&sort=sort`;

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Выполняем оба запроса одновременно / Gleichzeitiges Ausführen beider Anfragen
                const [ausbildungResponse, kombiResponse] = await Promise.all([
                    axios.get(KOMBI_AUSBILDUNGEN_URL),
                    axios.get(KOMBI_DETAIL_URL),
                ]);

                // Обработка Ausbildungen / Verarbeitung der Ausbildungen
                if (ausbildungResponse.data && ausbildungResponse.data.data && ausbildungResponse.data.data.length > 0) {
                    const fetchedAusbildungen = ausbildungResponse.data.data;
                    setAusbildungen(fetchedAusbildungen);

                    // Сбор всех Blocks и Fragen / Sammlung aller Blocks und Fragen
                    let allBlocks = [];
                    let allFragen = [];

                    fetchedAusbildungen.forEach((ausbildung) => {
                        if (ausbildung.Blocks) {
                            allBlocks = [...allBlocks, ...ausbildung.Blocks];
                        }
                        if (ausbildung.Fragen) {
                            allFragen = [...allFragen, ...ausbildung.Fragen];
                        }
                    });

                    setBlocks(allBlocks);
                    setFragen(allFragen);

                    // Если только один Ausbildung — выбираем его автоматически / Wenn nur eine Ausbildung vorhanden ist, wird diese automatisch ausgewählt
                    if (fetchedAusbildungen.length === 1) {
                        setSelectedAusbildungId(fetchedAusbildungen[0].id);
                        setSelectedAusbildungGW(!fetchedAusbildungen[0].Grundwissen);
                    }
                } else {
                    setError("Keine Ausbildungen für diese Kombination gefunden");
                }

                // Обработка данных Komibnation / Verarbeitung der Kombinationsdaten
                if (kombiResponse.data && kombiResponse.data.data) {
                    setCombinationData(kombiResponse.data.data);
                } else {
                    setError("Kombination nicht gefunden");
                }
            } catch (err) {
                setError("Fehler beim Laden der Daten");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [KOMBI_AUSBILDUNGEN_URL, KOMBI_DETAIL_URL]);

    const [activeSection, setActiveSection] = useState(null);
    const [hideTitleKlasse, isNotTop] = useState(null);
    const sections = useRef([]);

    const handleScroll = () => {
        const pageYOffset = window.scrollY;
        let newActiveSection = null;

        sections.current = document.querySelectorAll('[data-section]');

        sections.current.forEach((section) => {
            const sectionOffsetTop = section.offsetTop;
            const sectionHeight = section.offsetHeight;

            if (pageYOffset >= (sectionOffsetTop - 220) && pageYOffset < (sectionOffsetTop - 220) + sectionHeight) {
                newActiveSection = section.id;
            }
        });

        isNotTop(pageYOffset > 0 ? true : false);
        setActiveSection(newActiveSection);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (ausbildungen.length > 0 && !selectedAusbildungId) {
            setSelectedAusbildungId(ausbildungen[0].id);
            setSelectedAusbildungGW(!ausbildungen[0].Grundwissen);
        }
    }, [ausbildungen, selectedAusbildungId]);

    // Функция для переключения состояния вопроса / Funktion zum Umschalten des Zustands einer Frage
    const toggleQuestion = (qid) => {
        setOpenQuestions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(qid)) {
                newSet.delete(qid);
            } else {
                newSet.add(qid);
            }
            return newSet;
        });
    };

    // Обработка клика по кнопке "Alle anzeigen" / Verarbeitung des Klicks auf die Schaltfläche "Alle anzeigen"
    const handleToggleShowAll = () => {
        setShowAllQuestions((prev) => !prev);
    };

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <p className="error">{error}</p>;
    }

    if (ausbildungen.length === 0 && !combinationData) {
        return <p>Ausbildung und Kombination sind nicht verfügbar</p>;
    }

    // Пример сортировки / Beispiel für die Sortierung
    const sortItems = (items) => items.sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999));

    // Сортируем blocks, fragen / Sortieren von blocks und fragen
    const sortedBlocks = blocks.length > 0 ? sortItems(blocks) : [];
    const sortedFragen = fragen.length > 0 ? sortItems(fragen) : [];

    // Фильтрация blocks и fragen по выбранному Ausbildung / Filtern von blocks und fragen nach der ausgewählten Ausbildung
    const filteredBlocks = selectedAusbildungId
        ? sortedBlocks.filter((block) => block.Ausbildungen_id === selectedAusbildungId)
        : [];

    const filteredFragen = selectedAusbildungId
        ? sortedFragen.filter((frage) => frage.Ausbildungen_id === selectedAusbildungId)
        : [];

    // Текущий Ausbildung / Aktuelle Ausbildung
    const selectedAusbildung = ausbildungen.find(
        (ausbildung) => ausbildung.id === selectedAusbildungId
    );

    // Логика для вопросов (аналогично вашему ClassDetail) / Logik für Fragen (ähnlich wie in ClassDetail)
    const questionsToShow = showAllQuestions ? filteredFragen : filteredFragen.slice(0, 5);
    const hasMoreQuestions = filteredFragen.length > 5;

    // Пример: отображаем контент первого блока (если нужно) / Beispiel: Anzeige des Inhalts des ersten Blocks (falls erforderlich)
    const getFirstBlockContent = (ausbildungId) => {
        const ausbildungBlocks = sortedBlocks.filter((b) => b.Ausbildungen_id === ausbildungId);
        const firstBlock = ausbildungBlocks.find((block) => block.sort === 1) || ausbildungBlocks[0];
        return firstBlock ? firstBlock.item.Content : "";
    };

    // Пример получения иконки (если она есть) / Beispiel zum Abrufen eines Icons (falls vorhanden)
    const getIconUrl = (icon) => {
        if (!icon || !icon.filename_disk) return null;
        const baseUrl = 'https://admin.thirsty-shtern.91-151-16-181.plesk.page/assets/';
        return `${baseUrl}${icon.filename_disk}`;
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <>
        <header className="combo-header">
            <div className="container">
            {/* <Link to="/" className="back-button">← Zurück</Link> */}
                <button onClick={() => navigate(-1)} className="back-button">
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="var(--white)"><path d="m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z"/></svg>
                </button> {/* back page */}
                <h2 className={hideTitleKlasse ? 'hidden' : ''}>Kombi</h2>
                <h1>{combinationData?.Name}</h1>
            </div>
        </header>
        <div className="combo-detail" id="ausbildungen">

            <div className="ausbildung-main-info">
                {/* Если у комбинации есть какие-то поля типа Ausbildungsbeginn, Pruefungsantritt, здесь показываем / Wenn die Kombination Felder wie Ausbildungsbeginn oder Pruefungsantritt hat, hier anzeigen */}
                {/* Или описание самой Kombination / Oder Beschreibung der Kombination selbst */}
            </div>

            <div className="sticky-buttons">
                <nav>
                        <button
                            type="button"
                            className={(activeSection === 'ausbildungen' || activeSection === null) ? 'active' : ''}
                            onClick={() => scrollToSection('ausbildungen')}>
                            {ausbildungen.length > 1 ? 'Varianten' : 'Ausbildung'}
                        </button>
                        <button
                            type="button"
                            className={activeSection === 'termine' ? 'active' : ''}
                            onClick={() => scrollToSection('termine')}>
                            Kurse
                        </button>
                    </nav>
            </div>

            <div className="ausbildung-list">
                {ausbildungen.length > 1 && (
                    <p>Unsere Ausbildungsvarianten:</p>
                )}
                <ul>
                    {ausbildungen.map((ausbildung) => (
                        <li key={ausbildung.id} className="ausbildung-item">
                            <div className="ausbildung-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="35" height="35">
                                    <g transform="rotate(-90 12 12)">
                                        <path d="M12 2v12" stroke="rgb(22, 160, 211)" strokeWidth="3" fill="none" />
                                        <path d="M12 16l-6-6h12l-6 6z" fill="rgb(22, 160, 211)" />
                                    </g>
                                </svg>
                            </div>
                            <div>
                                <h3 className="ausbildung-name">{ausbildung.Name}</h3>
                                {ausbildung.Kurzbeschreibung && (
                                    <p style={{ margin: '0' }}>{ausbildung.Kurzbeschreibung}</p>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Если несколько Ausbildung / Wenn mehrere Ausbildungen vorhanden sind */}
            {ausbildungen.length > 1 && (
                <div className="ausbildung-selector">

                    {/* Кнопки выбора Ausbildung / Buttons zur Auswahl der Ausbildung */}
                    <div className="ausbildung-selector-buttons">
                        <div className="button-group">
                            {ausbildungen.map((ausbildung) => (
                                <button
                                    key={ausbildung.id}
                                    className={`orange-select ${selectedAusbildungId === ausbildung.id ? 'active' : ''
                                        }`}
                                    onClick={() => {
                                        setSelectedAusbildungId(ausbildung.id);
                                        setSelectedAusbildungGW(!ausbildung.Grundwissen);
                                    }}
                                >
                                    {ausbildung.Kurzname ? ausbildung.Kurzname : ausbildung.Name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Если выбран Ausbildung, показываем детали / Wenn eine Ausbildung ausgewählt ist, Details anzeigen */}
            {selectedAusbildungId && (
                <div className="ausbildung-details container">
                    {/* Отображаем сам выбранный Ausbildung / Anzeige der ausgewählten Ausbildung */}
                    {ausbildungen
                        .filter((a) => a.id === selectedAusbildungId)
                        .map((ausbildung) => (
                            <div key={ausbildung.id}>
                                {/* Иконка, если нужна / Icon, falls benötigt */}
                                {typeof ausbildung.Icon === 'object' && ausbildung.Icon.filename_disk && (
                                    <img
                                        src={getIconUrl(ausbildung.Icon)}
                                        alt={ausbildung.Name}
                                        className="klasse-detail-icon"
                                        loading="lazy"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/images/placeholder.png';
                                        }}
                                    />
                                )}

                                {/* Описание Ausbildung / Beschreibung der Ausbildung */}
                                {/* {ausbildung.Beschreibung && (
                                    <div
                                        className="klasse-detail-description"
                                        dangerouslySetInnerHTML={{ __html: ausbildung.Beschreibung }}
                                    />
                                )}
                                <div> */}
                                <div className="ausbildung-header">
                                    <h2>{ausbildung.Name}</h2>
                                    {ausbildung.Kurzbeschreibung && (
                                        <div
                                            className="klasse-detail-description"
                                            dangerouslySetInnerHTML={{ __html: ausbildung.Kurzbeschreibung }}
                                        />
                                    )}
                                
                                {/* </div> */}
            
                                {/* {klasseData?.Externer_Link ? (
                                    <a className="button pagelink" href={klasseData.Externer_Link} target="_blank" rel="noreferrer">
                                    Zur Website
                                    </a>
                                ) : ( */}
                                    <Link className="button pagelink" to={`/angebot/${ausbildung.id}`}>Angebot anfordern</Link>
                                {/* )} */}
                                </div>
                            </div>
                        ))}

                    {/* Блоки / Blocks */}
                    {filteredBlocks.length > 0 && (
                        <div className="klasse-detail-blocks">
                            {filteredBlocks.map((block, index) => (
                                <React.Fragment key={block.id}>
                                    {block.collection === "Ausbildungsverlauf" && (
                                        <div className="verlauf-block">
                                            {block.item?.Headline && <h3>{block.item.Headline}</h3>}
                                            {block.item?.Content && (
                                                <div
                                                    className="verlauf-block-content"
                                                    dangerouslySetInnerHTML={{ __html: block.item.Content }}
                                                />
                                            )}
                                            <div className="ablauf-container">
                                                {block.item.Ablauf.map((ablauf, index) => (
                                                    <React.Fragment key={ablauf.id}>
                                                        <div className={`ablauf-block block-${ablauf.collection}`}>
                                                            {ablauf.item?.Headline && <h4>{ablauf.item.Headline}</h4>}
                                                            {ablauf.item?.Content && (
                                                                <div
                                                                    className="ablauf-block-content"
                                                                    dangerouslySetInnerHTML={{ __html: ablauf.item.Content }}
                                                                />
                                                            )}
                                                        </div>
                                                    </React.Fragment>
                                                ))}
                                            </div>

                                        </div>

                                    )}
                                    {block.collection !== "Ausbildungsverlauf" && (
                                        <div className="klasse-block">
                                            {block.item?.Headline && <h4>{block.item.Headline}</h4>}
                                            {block.item?.Content && (
                                                <div
                                                    className="klasse-block-content"
                                                    dangerouslySetInnerHTML={{ __html: block.item.Content }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}

                    {/* ВОПРОСЫ / FRAGEN */}
                    {filteredFragen.length > 0 && selectedAusbildung && (
                        <div className="klasse-detail-fragen">
                            <h2>
                                Alles Wissenswerte zu <br />
                                <span className="spanBlau">
                                    {selectedAusbildung.Name}
                                </span>
                            </h2>
                            {questionsToShow.map((frageRelation, index) => {
                                const frageId = frageRelation.id;
                                const isOpen = openQuestions.has(frageId);
                                const frageObj = frageRelation.Fragen_id;
                                if (!frageObj) return null;

                                return (
                                    <div
                                        key={frageRelation.id}
                                        className={`klasse-frage ${isOpen ? 'open' : ''}`}
                                    >
                                        <div
                                            className="frage-header"
                                            onClick={() => toggleQuestion(frageId)}
                                            aria-expanded={isOpen}
                                            role="button"
                                            tabIndex="0"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    toggleQuestion(frageId);
                                                }
                                            }}
                                        >
                                            {/* Можно добавить порядковый номер / Optional kann eine fortlaufende Nummer hinzugefügt werden */}
                                            <div className="frage-content">
                                                <span className="frage-text">{frageObj.Frage}</span>
                                            </div>
                                            <div className={`checkmark ${isOpen ? 'open' : ''}`}>
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48">
                                                    <path d="M6 9l6 6 6-6" stroke="white" strokeWidth="2" fill="none" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className={`klasse-antwort ${isOpen ? 'open' : ''}`}>
                                            {frageObj.Antwort && (
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: frageObj.Antwort }}
                                                    className="antwort-content"
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {/* Кнопка "Alle anzeigen" / Schaltfläche "Alle anzeigen" */}
                            {hasMoreQuestions && !showAllQuestions && (
                                <button className="alle-anzeigen-button" onClick={handleToggleShowAll}>
                                    Alle anzeigen
                                </button>
                            )}
                            {/* Кнопка "Weniger anzeigen" / Schaltfläche "Weniger anzeigen*/}
                            {hasMoreQuestions && showAllQuestions && (
                                <button className="alle-anzeigen-button" onClick={handleToggleShowAll}>
                                    Weniger anzeigen
                                </button>
                            )}
                        </div>
                    )}

                    {/* Если у самой комбинации есть какое-то описание... / Falls die Kombination selbst eine Beschreibung hat... */}
                    {combinationData && combinationData.Beschreibung && (
                        <div className="klasse-detail-class-description">
                            <h4 className="description">Gesetzliche Bestimmungen:</h4>
                            <div
                                dangerouslySetInnerHTML={{ __html: combinationData.Beschreibung }}
                            />
                        </div>
                    )}
                </div>
            )}

                    <section id="termine" data-section>
                        <div className="termine-container">
                            <div style={{display:"flex" , alignItems:"center"}}>
                                {/* <h2 style={{ marginInline: "1.5rem" }}>Kurse für {selectedAusbildung.Name} </h2> */}
                                <h2 style={{ marginInline: "1.5rem" }}>Kurse für {combinationData?.Name} </h2>
                               {/*  <p>(Variante: {selectedAusbildungName})</p> */}
                            </div>
                        </div>
                        {console.log(combinationData?.Klassen.map(a => a.Klassen_id))}
                        {console.log(selectedAusbildungGW)}
                        <KursplanPage
                            key={selectedAusbildungGW}
                            smallKursplan={true}
                            prefilter={true}
                            selectedClasses={combinationData?.Klassen.map(a => a.Klassen_id)}
                            grundwissen={selectedAusbildungGW}
                        />
                    </section>
        </div>
        </>
    );
};

export default CombinationDetails;
